/**
 * Dashboard "Insights" tiles (NAU-266).
 *
 * Derives the four insight tiles from the same period-aware `/dashboard/status`
 * read the summary cards use (NAU-263): the completion-rate improvement reuses
 * the card delta (`completionRateDelta`), late-checks and most-active come from
 * the period metrics, and the overall band folds the live status items to their
 * worst band — no separate query.
 */

import type {
  DashboardMostActiveDto,
  DashboardStatusItemDto,
  DashboardStatusMetricsDto,
} from "@/generated/model";
import { DashboardStatusItemDtoBand } from "@/generated/model";
import { completionRateDelta } from "@/lib/dashboard/card-status";
import type { DashboardStatus } from "@/lib/dashboard/traffic-light";

export interface DashboardInsights {
  /** Completion-rate change vs the previous period (percentage points); null until both periods load. */
  completionImprovement: number | null;
  /** Top completer in the period (name + count), or null when none are user-attributed. */
  mostActive: DashboardMostActiveDto | null;
  /** Late checks this period and the change vs the previous period (negative = fewer). */
  lateChecks: { count: number; delta: number };
  /** Worst current live status across the nursery. */
  overallBand: DashboardStatus;
}

/** Worst band wins: ACTION > NEEDS_ATTENTION > ON_TRACK. NO_STATUS is ignored; empty → ON_TRACK. */
export function worstBand(items: DashboardStatusItemDto[]): DashboardStatus {
  let worst: DashboardStatus = "ON_TRACK";
  for (const item of items) {
    if (item.band === DashboardStatusItemDtoBand.ACTION) {
      return "ACTION";
    }
    if (item.band === DashboardStatusItemDtoBand.NEEDS_ATTENTION) {
      worst = "NEEDS_ATTENTION";
    }
  }
  return worst;
}

/**
 * Fold the current + previous period metrics (and the live items) into the four
 * insight tiles. The completion improvement uses the shared `completionRateDelta`
 * so it matches the NAU-263 summary card by construction.
 */
export function buildInsights(
  current: DashboardStatusMetricsDto,
  previous: DashboardStatusMetricsDto | undefined,
  items: DashboardStatusItemDto[],
): DashboardInsights {
  return {
    completionImprovement: completionRateDelta(
      current.completionRate,
      previous?.completionRate,
      previous?.totalDueCount,
    ),
    mostActive: current.mostActive ?? null,
    lateChecks: {
      count: current.lateChecksTotal,
      delta:
        current.lateChecksTotal -
        (previous?.lateChecksTotal ?? current.lateChecksTotal),
    },
    overallBand: worstBand(items),
  };
}
